import React, { useState, useMemo } from 'react';
import { FiSearch, FiChevronLeft } from 'react-icons/fi';
import { motion } from 'framer-motion';
import useFetchAllCategories from '../../Hooks/useFetchAllCategories';
import type { Category, Subcategory } from '../../Hooks/useFetchAllCategories';
import BottomSheet from './BottomSheet';

const CategoriesCard: React.FC = () => {
    const [sheetOpen, setSheetOpen] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
    const [search, setSearch] = useState('');
    const { categories, subcategories, loading, error } = useFetchAllCategories();

    // Generate random pastel color
    const getRandomColor = () => {
        const hue = Math.floor(Math.random() * 360);
        return `hsl(${hue}, 70%, 90%)`;
    };

    // Filter subcategories by selected category and search
    const filteredSubcategories = useMemo(() => {
        if (!selectedCategory) return [];
        return subcategories
            .filter(sc => sc.category_id === selectedCategory.id)
            .filter(sc => sc.name.toLowerCase().includes(search.toLowerCase()));
    }, [selectedCategory, subcategories, search]);

    // Handle category tap
    const handleCategoryTap = (cat: Category) => {
        setSelectedCategory(cat);
        setSheetOpen(true);
        setSearch('');
    };

    // Handle subcategory tap
    const handleSubcategoryTap = (subcat: Subcategory) => {
        if (selectedCategory) {
            const categoryName = encodeURIComponent(selectedCategory.name);
            const subcategoryName = encodeURIComponent(subcat.name);
            window.location.href = `/search/${categoryName}/${subcategoryName}`;
            setSheetOpen(false);
        }
    };

    

    if (loading) return (
        <div className="w-full p-3">
            <div className="grid grid-cols-4 gap-3">
                {[...Array(8)].map((_, index) => (
                    <div
                        key={index}
                        className="flex flex-col items-center p-3 rounded-xl bg-gray-100 animate-pulse"
                    >
                        <div className="w-12 h-12 rounded-full bg-gray-200 mb-2" />
                        <div className="h-3 w-16 bg-gray-200 rounded-full" />
                    </div>
                ))}
            </div>
        </div>
    );
    if (error) return (
        <div className="w-full p-3">
            <div className="flex flex-col items-center justify-center p-6 bg-gray-50 rounded-xl">
                <div className="w-16 h-16 rounded-full bg-gray-200 mb-3 animate-pulse" />
                <div className="h-4 w-32 bg-gray-200 rounded-full mb-2 animate-pulse" />
                <div className="h-3 w-24 bg-gray-200 rounded-full animate-pulse" />
            </div>
        </div>
    );

    return (
        <div className="w-full">
            {/* Grid layout for categories */}
            <div className="grid grid-cols-4 gap-3 px-2.5 py-3">
                {categories.map((cat, index) => (
                    <motion.button
                        key={cat.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="flex flex-col items-center p-2 rounded-xl shadow-sm hover:shadow-md transition-all"
                        style={{ backgroundColor: getRandomColor() }}
                        onClick={() => handleCategoryTap(cat)}
                    >
                        <motion.img
                            whileHover={{ rotate: 5 }}
                            src={cat.image || '/default-category-image.png'}
                            alt={cat.name}
                            className="w-12 h-12 rounded-full object-cover border border-gray-200 mb-2"
                        />
                        <span className="text-xs font-medium text-content-default text-center min-h-[2.5rem] px-1 break-words">
                            {cat.name}
                        </span>
                    </motion.button>
                ))}
            </div>

            {/* BottomSheet for subcategories */}
            <BottomSheet
                open={sheetOpen}
                onClose={() => setSheetOpen(false)}
                title={
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setSheetOpen(false)}
                            className="text-xl"
                            aria-label="Retour"
                        >
                            <FiChevronLeft />
                        </button>
                        <span className="truncate max-w-[200px]">{selectedCategory?.name}</span>
                    </div>
                }
            >
                <div className="mb-2">
                    <div className="flex items-center bg-gray-100 rounded-lg px-2 py-1">
                        <FiSearch className="text-gray-400 mr-2" />
                        <input
                            type="text"
                            placeholder="Rechercher une sous-catégorie"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            className="bg-transparent outline-none flex-1 py-1 text-sm"
                        />
                    </div>
                </div>
                <div className="flex flex-col gap-2 max-h-72 overflow-y-auto">
                    {filteredSubcategories.length === 0 && (
                        <div className="text-gray-400 text-center py-8">Aucune sous-catégorie</div>
                    )}
                    {filteredSubcategories.map((subcat, index) => (
                        <motion.button
                            key={subcat.id}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.05 }}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 hover:bg-blue-50 transition"
                            onClick={() => handleSubcategoryTap(subcat)}
                        >
                            <img
                                src={subcat.image || '/default-subcategory-image.png'}
                                alt={subcat.name}
                                className="w-10 h-10 rounded object-cover border border-gray-200"
                            />
                            <span className="text-sm font-medium text-gray-800 flex-1 text-left truncate">
                                {subcat.name}
                            </span>
                            <span className="bg-blue-100 text-blue-600 text-xs px-2 py-0.5 rounded-full">
                                {subcat.adCount ?? 0} articles
                            </span>
                        </motion.button>
                    ))}
                </div>
            </BottomSheet>
        </div>
    );
};

export default CategoriesCard;